import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, CheckCircle } from 'lucide-react';
import api from '@/api/api';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: '' });
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name) newErrors.name = 'Le nom est requis';
    if (!formData.email) {
      newErrors.email = 'L\'email est requis';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email invalide';
    }
    if (!formData.subject) newErrors.subject = 'Le sujet est requis';
    if (!formData.message) newErrors.message = 'Le message est requis';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    // Google Analytics event
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'devis_pac_demande', {
        'event_category': 'PAC_Conversion',
        'event_label': 'Lead_Qualifie',
        'value': 15000
      });
    }
	  // Facebook Pixel event
    if (window.fbq) {
      window.fbq('track', 'InitiateCheckout', {
        content_name: 'Devis PAC Air-Eau',
        value: 15000,
        currency: 'EUR'
      });
    }
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      await api.sendContact({
        name: formData.name,
        email: formData.email,
        phone: formData.phone || undefined,
        subject: formData.subject,
        message: formData.message,
      });
   // Track GA event
      if (window.gtag) {
        window.gtag('event', 'contact_form_submitted', {
          event_category: 'Contact',
          subject: formData.subject
        });
      }
      setSuccess(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
      });

      setTimeout(() => setSuccess(false), 5000);
    } catch (error) {
      console.error('Error sending contact form:', error);
      alert('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ContactPage min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Contactez-nous</h1>
            <div className="section-decorator"></div>
            <p className="text-xl text-gray-600 mt-4">
              Notre équipe d'experts RGE est à votre écoute
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Coordonnées */}
            <div>
              <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Nos Coordonnées</h2>
                
                <div className="space-y-6">
                  <div className="flex items-start gap-4" data-testid="contact-info">
                    <div className="w-12 h-12 bg-[#2D9A68]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone className="text-[#2D9A68]" size={24} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Téléphone</h3>
                      <a href="tel:0188617508" className="text-[#2D9A68] hover:underline text-lg">
                        01 88 61 75 08
                      </a>
                      <p className="text-sm text-gray-500 mt-1">Lun-Jeu : 10h-17h | Ven : 10h-15h</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4" data-testid="contact-info">
                    <div className="w-12 h-12 bg-[#2F5BA0]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="text-[#2F5BA0]" size={24} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Email</h3>
                      <a href="mailto:contact@climatpac81.fr" className="text-[#2F5BA0] hover:underline">
                        contact@climatpac81.fr
                      </a>
                      <p className="text-sm text-gray-500 mt-1">Réponse sous 24h</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4" data-testid="contact-info">
                    <div className="w-12 h-12 bg-[#E6A835]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="text-[#E6A835]" size={24} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Zone d'intervention</h3>
                      <p className="text-gray-700">Intervention nationale</p>
                      <p className="text-sm text-gray-500 mt-1">Nous intervenons partout en France</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Certifications */}
              <div className="bg-gradient-to-br from-[#2D9A68] to-[#2F5BA0] text-white rounded-2xl shadow-lg p-8">
                <h2 className="text-2xl font-bold mb-6">Certifications & Garanties</h2>
                
                <ul className="space-y-3">
                  <li className="flex items-center gap-2">
                    <CheckCircle size={20} />
                    <span>Certifié Qualibat RGE</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle size={20} />
                    <span>Certifié QualiPAC RGE</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle size={20} />
                    <span>Certifié Ventilation+ RGE</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle size={20} />
                    <span>Certifié Qualisol RGE</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle size={20} />
                    <span>Garantie décennale</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle size={20} />
                    <span>Assurance RC Pro</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Formulaire */}
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Envoyez-nous un message</h2>

              {success && (
                <div className="bg-green-50 border border-green-200 text-green-800 rounded-lg p-4 mb-6 flex items-center gap-3" data-testid="success-message">
                  <CheckCircle className="text-green-700" size={24} />
                  <div>
                    <p className="font-semibold">Message envoyé avec succès !</p>
                    <p className="text-sm">Nous vous répondrons dans les plus brefs délais.</p>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Nom et Prénom <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 form-input rounded-lg"
                    placeholder="Jean Dupont"
                    data-testid="input-name"
                  />
                  {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 form-input rounded-lg"
                    placeholder="jean.dupont@email.com"
                    data-testid="input-email"
                  />
                  {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Téléphone <span className="text-gray-400 text-xs">(optionnel)</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 form-input rounded-lg"
                    placeholder="06 12 34 56 78"
                    data-testid="input-phone"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Sujet <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className="w-full px-4 py-3 form-input rounded-lg"
                    data-testid="input-subject"
                  >
                    <option value="">Sélectionnez un sujet</option>
                    <option value="Demande de devis">Demande de devis</option>
                    <option value="Renseignements sur les aides">Renseignements sur les aides</option>
                    <option value="Question technique">Question technique</option>
                    <option value="Prise de rendez-vous">Prise de rendez-vous</option>
                    <option value="Autre">Autre</option>
                  </select>
                  {errors.subject && <p className="text-red-500 text-sm mt-1">{errors.subject}</p>}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="6"
                    className="w-full px-4 py-3 form-input rounded-lg"
                    placeholder="Décrivez votre projet ou posez votre question..."
                    data-testid="input-message"
                  ></textarea>
                  {errors.message && <p className="text-red-500 text-sm mt-1">{errors.message}</p>}
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#2D9A68] text-white px-8 py-4 rounded-lg font-bold hover:bg-[#237852] transition-all inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="submit-button"
                >
                  {loading ? (
                    'Envoi en cours...'
                  ) : (
                    <>
                      Envoyer le message <Send size={20} />
                    </>
                  )}
                </button>
				                {/* RGPD Notice */}
                <p className="text-xs text-gray-500 text-center mt-4">
                  Les informations recueillies via ce formulaire sont utilisées uniquement pour répondre à votre demande.
                  Pour en savoir plus sur la gestion de vos données, consultez notre{' '}
                  <a href="/politique-confidentialite" className="text-[#2D9A68] underline hover:text-[#237852]">
                    Politique de confidentialité
                  </a>.
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;